import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import FeedbackRenderer from 'components/InstructorFeedback/FeedbackRenderer';
import Header from 'components/InstructorFeedback/Header';
import { useMemo } from 'react';
import {
    Box,
    Container,
    Dropdown,
    IconButton,
    PDFViewer,
    RichTextArea,
    SheetViewer,
    Stack,
    styled,
    Text,
} from 'shell/components';
import theme from 'shell/themes';
import { getS3FileURL } from 'utils/helper';

const Tablet = ({
    form,
    setForm,
    selectedFeedbackId,
    setSelectedFeedbackId,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    currentFeedbackItem,
    feedbackItems,
    currentFeedbackItemIndex,
    highlights,
    onClickPrevAttempt,
    onClickNextAttempt,
    onSelect,
    updateFeedback,
}) => {
    const url = useMemo(() => getS3FileURL(currentFeedbackItem.submission), [currentFeedbackItem?.submission]);

    return (
        <RootContainer>
            <Header />
            <ContentStack>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && typeof currentFeedbackItem.submission !== 'string' && (
                        <BannerBox>
                            <Text color={'inherit'}>Currently viewing annotations for feedback.</Text>
                        </BannerBox>
                    )}
                    {typeof currentFeedbackItem.submission === 'string' ? (
                        <Stack sx={{ height: '100%', p: 3 }}>
                            <CustomRichTextArea readOnly value={currentFeedbackItem.submission} onChange={null} />
                        </Stack>
                    ) : currentFeedbackItem.submission.type === 'application/pdf' ? (
                        <PDFViewer
                            highlights={highlights}
                            isFullScreen={isDocumentFullScreen}
                            url={url}
                            inlineComments={currentFeedbackItem.inline_comments}
                            selectedInlineCommentId={null}
                            setInlineComments={() => {
                                return null;
                            }}
                            setSelectedInlineCommentId={() => {
                                return null;
                            }}
                            setIsFullScreen={setIsDocumentFullScreen}
                            isStudent={true}
                        />
                    ) : (
                        <SheetViewer highlights={highlights} url={getS3FileURL(currentFeedbackItem.submission)} />
                    )}
                </TopStack>
                <BottomStack isDocumentFullScreen={isDocumentFullScreen}>
                    <Stack sx={{ py: 2, px: 3 }} direction={'row'} justifyContent={'space-between'}>
                        <Text
                            component={'h1'}
                            noWrap
                            title={currentFeedbackItem.submission?.name}
                            variant={'sh2'}
                            weight={'semibold'}
                        >
                            {currentFeedbackItem.submission?.name}
                        </Text>
                        <Stack sx={{ flexShrink: 0 }} alignItems={'center'} direction={'row'} spacing={4}>
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <IconButton
                                    disabled={!currentFeedbackItemIndex}
                                    kind={'chevronLeft'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={onClickPrevAttempt}
                                />
                                <Text>
                                    Attempt {currentFeedbackItemIndex + 1}/{feedbackItems.length}
                                </Text>
                                <IconButton
                                    disabled={currentFeedbackItemIndex === feedbackItems.length - 1}
                                    kind={'chevronRight'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={onClickNextAttempt}
                                />
                            </Stack>
                            <Dropdown
                                id={'instant-feedback-dropdown'}
                                options={[
                                    // { key: 'reset', value: 'Reset this attempt', icon: 'refresh' },
                                    { key: 'copy', value: 'Copy all feedback', icon: 'copy' },
                                    { key: 'delete', value: 'Delete all feedback', icon: 'delete' },
                                ]}
                                rootComponent={<IconButton kind={'moreHorizontal'} size={'small'} variant={'square'} />}
                                onSelect={onSelect}
                            />
                        </Stack>
                    </Stack>
                    {currentFeedbackItem.is_failed || typeof form.feedback === 'string' ? (
                        <LegacyFeedbackRenderer
                            currentSubmission={currentFeedbackItem}
                            form={form}
                            setForm={(func) => {
                                setForm(func);
                                updateFeedback(func(form));
                            }}
                            setShowFailedDialog={null}
                        />
                    ) : (
                        <FeedbackRenderer
                            currentFeedbackItem={currentFeedbackItem}
                            form={form}
                            setForm={(func) => {
                                setForm(func);
                                updateFeedback(func(form));
                            }}
                            selectedFeedbackId={selectedFeedbackId}
                            setSelectedFeedbackId={setSelectedFeedbackId}
                        />
                    )}
                </BottomStack>
            </ContentStack>
        </RootContainer>
    );
};

const RootContainer = styled(Container)(() => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '81px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 81px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? 'calc(100% - 24px)' : '48%',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        height: '100%',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        height: isDocumentFullScreen ? 0 : '52%',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'height 150ms',
    };
});

export default Tablet;
